import {
  classLessonTimeSchema,
  createBaseResponseSchema,
  curriculumTypeSchema,
  lessonCountPerMonthSchema,
  lessonMonthSchema,
  paymentTypeSchema,
  promotionTypeSchema,
  ticketLangTypeSchema,
} from '@server/presentation/schema'
import { z } from 'zod'

export const rawSubscribeTicketSchema = z.object({
  id: z.string(),
  product_kind: z.string().nullable().optional(),
  country_code: z.enum(['KR', 'JP']).nullable().optional(),
  total_lesson_count: z.number().int().min(1).max(200).nullable().optional(),
  validity_months: z.number().int().min(1).max(12).nullable().optional(),
  lang_type: ticketLangTypeSchema,
  sub_name: z.string(),
  lesson_month: z.union([lessonMonthSchema, z.number().int().min(1).max(12)]),
  lesson_count_per_month: z.union([lessonCountPerMonthSchema, z.number().int().min(1).max(200)]),
  origin_price: z.number(),
  sub_price: z.number(),
  discount_rate: z.number(),
  lesson_time: classLessonTimeSchema,
  cre_date_time: z.string(),
  curriculum_type: curriculumTypeSchema.optional().default('BASIC'),
  payment_type: paymentTypeSchema,
  promotion_type: promotionTypeSchema.optional().default('NONE'),
})

export const getRawSubscribeTicketDetailResponseSchema = rawSubscribeTicketSchema
export const getRawSubscribeTicketsResponseSchema = createBaseResponseSchema(z.array(rawSubscribeTicketSchema))

export const subscribeTicketSchema = z.object({
  id: rawSubscribeTicketSchema._def.shape().id,
  productKind: rawSubscribeTicketSchema._def.shape().product_kind,
  countryCode: rawSubscribeTicketSchema._def.shape().country_code,
  totalLessonCount: rawSubscribeTicketSchema._def.shape().total_lesson_count,
  validityMonths: rawSubscribeTicketSchema._def.shape().validity_months,
  langType: rawSubscribeTicketSchema._def.shape().lang_type,
  subName: rawSubscribeTicketSchema._def.shape().sub_name,
  lessonMonth: rawSubscribeTicketSchema._def.shape().lesson_month,
  lessonCountPerMonth: rawSubscribeTicketSchema._def.shape().lesson_count_per_month,
  originPrice: rawSubscribeTicketSchema._def.shape().origin_price,
  subPrice: rawSubscribeTicketSchema._def.shape().sub_price,
  discountRate: rawSubscribeTicketSchema._def.shape().discount_rate,
  lessonTime: rawSubscribeTicketSchema._def.shape().lesson_time,
  creDateTime: rawSubscribeTicketSchema._def.shape().cre_date_time,
  paymentType: rawSubscribeTicketSchema._def.shape().payment_type,
  curriculumType: rawSubscribeTicketSchema._def.shape().curriculum_type.optional().default('BASIC'),
  promotionType: rawSubscribeTicketSchema._def.shape().promotion_type.optional().default('NONE'),
  productId: z.string().nullable().optional(),
  currency: z.string().nullable().optional(),
  paymentAmount: z.number().int().nonnegative().nullable().optional(),
  finalPaymentAmount: z.number().int().nonnegative().nullable().optional(),
  viewPrice: z.number().nullable().optional(),
  viewDiscountRate: z.number().nullable().optional(),
  finalPrice: z.number().nullable().optional(),
  finalDiscountRate: z.number().nullable().optional(),
  appliedCoupon: z
    .object({
      couponId: z.string(),
      title: z.string(),
      discountType: z.enum(['FIXED', 'PERCENTAGE']),
      discountAmount: z.number(),
      discountAmountMax: z.number(),
      discountApplicationType: z.enum(['RECURRING', 'LIMITED']).optional(),
      discountedPaymentCount: z.number().optional(),
    })
    .nullable()
    .optional(),
  isBest: z.boolean(),
  description: z.array(z.string()).optional().default([]),
  eventDescription: z.array(z.string()).optional().default([]),
})
export const getSubscribeTicketsResponseSchema = z.array(subscribeTicketSchema)

export type RawSubscribeTicket = z.infer<typeof rawSubscribeTicketSchema>
export type GetRawSubscribeTicketDetailResponse = z.infer<typeof getRawSubscribeTicketDetailResponseSchema>
export type GetRawSubscribeTicketsResponse = z.infer<typeof getRawSubscribeTicketsResponseSchema>
export type SubscribeTicket = z.infer<typeof subscribeTicketSchema>
export type GetSubscribeTicketsResponse = z.infer<typeof getSubscribeTicketsResponseSchema>
